import * as React from "react";
import { Box, Button, IconButton, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { CheckCircleIcon } from "@phosphor-icons/react/dist/ssr/CheckCircle";
import { UploadSimpleIcon } from "@phosphor-icons/react/dist/ssr/UploadSimple";
import { XIcon } from "@phosphor-icons/react/dist/ssr/X";

import type { BriefDocumentKind } from "@/hooks/api/use-campaign-briefs";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";

import { DOCUMENTS, formatBytes } from "../constants";

interface FileRowProps {
	kind: BriefDocumentKind;
	file: File | undefined;
	onChange: (file: File | null) => void;
	error?: string;
	/** Why it is asked for, e.g. "needed for the new agent". */
	note?: string;
	/** For the PO and SOW: the client's answer to "Required?". */
	required?: boolean | null;
	onRequiredChange?: (required: boolean) => void;
	requiredError?: string;
}

/** One file on the request: what it is, the file once chosen, and for paperwork whether it is required. */
export function FileRow({
	kind,
	file,
	onChange,
	error,
	note,
	required,
	onRequiredChange,
	requiredError,
}: FileRowProps): React.JSX.Element {
	const input = React.useRef<HTMLInputElement>(null);
	const { label, accept, hint } = DOCUMENTS[kind];
	const problem = Boolean(error || requiredError);

	return (
		<Box>
			<Stack
				direction={{ xs: "column", sm: "row" }}
				spacing={1.5}
				sx={{
					alignItems: { sm: "center" },
					px: 2,
					py: 1.5,
					borderRadius: 1.5,
					border: file ? "1px solid" : "1.5px dashed",
					borderColor: problem ? "error.main" : file ? "success.main" : "var(--mui-palette-divider)",
				}}
			>
				<Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
					<Typography variant="subtitle2">
						{label}
						{note ? (
							<Typography component="span" variant="body2" color="text.secondary">
								{" "}
								· {note}
							</Typography>
						) : null}
					</Typography>
					{file ? (
						<Typography variant="body2" color="success.main" noWrap title={file.name}>
							{file.name} · {formatBytes(file.size)}
						</Typography>
					) : (
						<Typography variant="caption" color="text.secondary">
							{hint}
						</Typography>
					)}
				</Box>

				{onRequiredChange ? (
					<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
						<Typography variant="body2" color="text.secondary" id={`${kind}-required`}>
							Required?
						</Typography>
						<ToggleButtonGroup
							exclusive
							size="small"
							aria-labelledby={`${kind}-required`}
							value={required === null || required === undefined ? null : String(required)}
							onChange={(_, next: string | null) => next !== null && onRequiredChange(next === "true")}
						>
							<ToggleButton value="true" sx={{ px: 1.75, textTransform: "none" }}>
								Yes
							</ToggleButton>
							<ToggleButton value="false" sx={{ px: 1.75, textTransform: "none" }}>
								No
							</ToggleButton>
						</ToggleButtonGroup>
					</Stack>
				) : null}

				{file ? (
					<Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
						<CheckCircleIcon size={20} color="var(--mui-palette-success-main)" weight="fill" />
						<IconButton size="small" aria-label={`Remove ${label}`} onClick={() => onChange(null)}>
							<XIcon />
						</IconButton>
					</Stack>
				) : (
					<Button
						size="small"
						variant="outlined"
						startIcon={<UploadSimpleIcon />}
						onClick={() => input.current?.click()}
						sx={{ whiteSpace: "nowrap" }}
					>
						Choose file
					</Button>
				)}
				<input
					ref={input}
					type="file"
					hidden
					accept={accept}
					onChange={(event) => {
						onChange(event.target.files?.[0] ?? null);
						event.target.value = "";
					}}
				/>
			</Stack>
			{requiredError ? <CustomFormHelperError text={requiredError} /> : null}
			{error ? <CustomFormHelperError text={error} /> : null}
		</Box>
	);
}
